import { useLazyQuery } from "@apollo/react-hooks";
import placeholderImg from "@assets/images/placeholder255x255.png";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import { BulkStockErrorFragment } from "@saleor/fragments/types/BulkStockErrorFragment";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { StockErrorFragment } from "@saleor/fragments/types/StockErrorFragment";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useOnSetDefaultVariant from "@saleor/hooks/useOnSetDefaultVariant";
import useShop from "@saleor/hooks/useShop";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { commonMessages } from "@saleor/intl";
import { SearchCornermarks_search_edges_node } from "@saleor/searches/types/SearchCornermarks";
import useCornermarkSearch from "@saleor/searches/useCornermarkSearch";
import useExtrachargeSearch from "@saleor/searches/useExtrachargeSearch";
import { ReorderEvent } from "@saleor/types";
import { CornerMarkTypeEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React, { useEffect, useState } from "react";
import { MutationFetchResult } from "react-apollo";
import { useIntl } from "react-intl";
import { arrayMove } from "react-sortable-hoc";

import {
  decimal,
  joinDateTime,
  maybe,
  splitDateTime,
  weight
} from "../../misc";
import ProductVariantDeleteDialog from "../components/ProductVariantDeleteDialog";
import ProductVariantPage, {
  ProductVariantPageSubmitData
} from "../components/ProductVariantPage";
import {
  useProductSkuExtraChargeCategoryMutation,
  useProductVariantReorderMutation,
  useProductVariantSetAvailabilityForPurchase,
  useVariantDeleteMutation,
  useVariantImageCreateMutation,
  useVariantImageDeleteMutation,
  useVariantImageSetFirstMutation,
  useVariantImagesReorder,
  useVariantImageVisibleEditByUrlMutation,
  useVariantS3ImageFetchMutation,
  // useVariantImageAssignMutation,
  // useVariantImageUnassignMutation,
  useVariantUpdateMutation
} from "../mutations";
// import { useInstagramsByProductsId, useProductVariantQuery } from "../queries";
import {
  getErpProductVariant,
  useExtraChargeCategoryDataQuery,
  useProductVariantQuery,
  useSkuExtraChargeCategoryQuery
} from "../queries";
import { ProductVariantDetails_productVariant } from "../types/ProductVariantDetails";
import {
  ProductVariantSetAvailabilityForPurchase,
  ProductVariantSetAvailabilityForPurchaseVariables
} from "../types/ProductVariantSetAvailabilityForPurchase";
import { VariantImageCreateVariables } from "../types/VariantImageCreate";
import { VariantImageReorderVariables } from "../types/VariantImageReorder";
import { VariantUpdate_productVariantUpdate_errors } from "../types/VariantUpdate";
import {
  productUrl,
  productVariantAddUrl,
  productVariantEditUrl,
  ProductVariantEditUrlDialog,
  ProductVariantEditUrlQueryParams,
  variantImageUrl
} from "../urls";
import { mapFormsetStockToStockInput } from "../utils/data";
import { getProductVariantAvailabilityVariables } from "../utils/handlers";
import { createVariantReorderHandler } from "./ProductUpdate/handlers";

interface ProductUpdateProps {
  variantId: string;
  productId: string;
  params: ProductVariantEditUrlQueryParams;
}

export const ProductVariant: React.FC<ProductUpdateProps> = ({
  variantId,
  productId,
  params
}) => {
  const shop = useShop();
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const {
    loadMore: loadMoreExtracharges,
    search: searchExtracharge,
    result: searchExtrachargeOpts
  } = useExtrachargeSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA
    }
  });

  const {
    loadMore: loadMoreCornermarks,
    search: searchCornermark,
    result: searchCornermarkOpts
  } = useCornermarkSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      type: CornerMarkTypeEnum.NORMAL
    }
  });

  const [errors, setErrors] = useState<
    VariantUpdate_productVariantUpdate_errors[]
  >([]);
  useEffect(() => {
    setErrors([]);
  }, [variantId]);
  const skuExtraChargeCategory = useSkuExtraChargeCategoryQuery({
    variables: {
      variantId
    }
  });
  const [
    activeSkuExtraChargeCategory
  ] = useProductSkuExtraChargeCategoryMutation({
    onCompleted: data => {
      const errors = data?.productSkuExtraChargeCategory?.errors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "update active success",
            description: "activeSkuExtraChargeCategory"
          })
        });
      }
      skuExtraChargeCategory.refetch();
    }
  });
  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });

  const { data, loading, refetch } = useProductVariantQuery({
    displayLoader: true,
    variables: {
      id: variantId
    }
  });
  const [newSku, setNewSku] = useState<string>(data?.productVariant?.sku || "");
  useEffect(() => {
    setNewSku(data?.productVariant?.sku);
  }, [data]);

  const extraChargeCategoryData = useExtraChargeCategoryDataQuery({
    variables: {
      sku: newSku
    }
  });

  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const [
    setProductVariantAvailability,
    productVariantAvailabilityOpts
  ] = useProductVariantSetAvailabilityForPurchase({
    onCompleted: data => {
      const errors = data?.productVariantSetAvailabilityForPurchase?.errors;
      if (errors?.length === 0) {
        const updatedProductVariant =
          data?.productVariantSetAvailabilityForPurchase?.variant;
        setProductVariant(variant => ({
          ...variant,
          availableForPurchase: updatedProductVariant.availableForPurchase,
          isAvailableForPurchase: updatedProductVariant.isAvailableForPurchase,
          preOrder: updatedProductVariant.preOrder,
          isPreOrder: updatedProductVariant.isPreOrder
        }));
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Variant availability updated",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ProductVariantEditUrlDialog,
    ProductVariantEditUrlQueryParams
  >(
    navigate,
    params => productVariantEditUrl(productId, variantId, params),
    params
  );

  const handleBack = () => navigate(productUrl(productId));

  const [deleteVariant, deleteVariantOpts] = useVariantDeleteMutation({
    onCompleted: data => {
      if (data.productVariantDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Variant removed"
          })
        });
        navigate(productUrl(productId));
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Variant has been sold/added and cannot be deleted."
          })
        });
        closeModal();
      }
    }
  });
  const [updateVariant, updateVariantOpts] = useVariantUpdateMutation({
    onCompleted: data => {
      if (data.productVariantUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
      setErrors(data.productVariantUpdate.errors);
    }
  });

  const [variant, setProductVariant] = useStateFromProps(data?.productVariant);

  if (variant === null) {
    return <NotFoundPage onBack={handleBack} />;
  }

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const onSetDefaultVariant = useOnSetDefaultVariant(productId, variant);

  const handleVariantReorder = createVariantReorderHandler(
    variant?.product,
    variables => reorderProductVariants({ variables })
  );

  const [
    createVariantImage,
    createVariantImageOpts
  ] = useVariantImageCreateMutation({
    onCompleted: data => {
      const imageError = data.variantImageCreate.errors.find(
        error => error.field === ("image" as keyof VariantImageCreateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
    }
  });
  const [deleteProductImage] = useVariantImageDeleteMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });

  const [
    variantS3ImageFetch,
    variantS3ImageFetchOpts
  ] = useVariantS3ImageFetchMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });

  const [
    variantImageSetFirst,
    variantImageSetFirstOpts
  ] = useVariantImageSetFirstMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });

  const [
    reorderVariantImages,
    reorderVariantImagesOpts
  ] = useVariantImagesReorder({});

  const disableFormSave =
    loading ||
    deleteVariantOpts.loading ||
    updateVariantOpts.loading ||
    createVariantImageOpts.loading ||
    reorderVariantImagesOpts.loading ||
    productVariantAvailabilityOpts.loading ||
    variantS3ImageFetchOpts.loading ||
    variantImageSetFirstOpts.loading ||
    reorderProductVariantsOpts.loading;

  const handleImageDelete = (id: string) => () =>
    deleteProductImage({ variables: { id } });

  const handleImageEdit = (imageId: string) => () =>
    navigate(variantImageUrl(productId, variantId, imageId));

  const handleImageUpload = createImageUploadHandler(variantId, variables =>
    createVariantImage({ variables })
  );

  const handleImageFetch = (id: string) => () =>
    variantS3ImageFetch({ variables: { variantId: id } });

  const handleImageSetFirst = (imageId: string) => () =>
    variantImageSetFirst({ variables: { variantId: variant?.id, imageId } });

  const handleImageReorder = createImageReorderHandler(variant, variables =>
    reorderVariantImages({ variables })
  );

  const [extraChargeCategory, setExtraChargeCategory] = useState<string>(
    variant?.extraChargeCategory?.categoryName || null
  );
  useEffect(() => {
    setExtraChargeCategory(variant?.extraChargeCategory?.categoryName);
  }, [variant]);
  function createUpdateHandler(
    variant: ProductVariantDetails_productVariant,
    setProductVariantAvailability: (
      variables: ProductVariantSetAvailabilityForPurchaseVariables
    ) => Promise<MutationFetchResult<ProductVariantSetAvailabilityForPurchase>>
  ) {
    return async (data: ProductVariantPageSubmitData) => {
      const result = await updateVariant({
        variables: {
          addStocks: data.addStocks.map(mapFormsetStockToStockInput),
          attributes: data.attributes.map(attribute => ({
            id: attribute.id,
            values: [attribute.value]
          })),
          skuAttributes: data.skuAttributes.map(attribute => ({
            id: attribute.id,
            values: [attribute.value]
          })),
          costPrice: decimal(data.costPrice),
          id: variantId,
          price: decimal(data.price),
          cornermark: ((data.cornermark as undefined) as SearchCornermarks_search_edges_node)
            .id
            ? ((data.cornermark as undefined) as SearchCornermarks_search_edges_node)
                .id
            : data.cornermark,
          extraChargeCategory,
          removeStocks: data.removeStocks,
          sku: data.sku,
          stocks: data.updateStocks.map(mapFormsetStockToStockInput),
          trackInventory: data.trackInventory,
          weight: weight(data.weight),
          // extraCharge: data.extraCharge,
          fromColor: data.fromColor,
          colorThumbnail: data.colorThumbnail,
          isTwoColor: data.isTwoColor,
          toColor: data.toColor,
          shortDescription: data.shortDescription,
          fullDescription: data.fullDescription,
          skuName: data.skuName,
          describe: data.describe,
          USP: data.USP,
          slug: data.slug,
          colorTagText: data.colorTagText,
          colorTagBackground: data.colorTagBackground,
          colorTagTextColor: data.colorTagTextColor,
          // colorTagIcon: data.colorTagIcon,
          seo: {
            description: data.seoDescription,
            title: data.seoTitle,
            keyword: data.seoKeyword
          },
          framesImg: data.framesImg,
          framesVideo: data.framesVideo,
          isPublished: data.isPublished || !!data.publicationDate,
          publicationDate:
            data.publicationDate !== "" ? data.publicationDate : null
        }
      });
      let errors: Array<
        ProductErrorFragment | StockErrorFragment | BulkStockErrorFragment
      >;

      errors = [
        ...result.data?.productVariantStocksCreate.errors,
        ...result.data?.productVariantStocksDelete.errors,
        ...result.data?.productVariantStocksUpdate.errors,
        ...result.data?.productVariantUpdate.errors
      ];

      const {
        isAvailableForPurchase,
        availableForPurchaseDate,
        availableForPurchaseTime,
        isPreOrder,
        preOrderDate,
        preOrderTime
      } = data;

      if (
        isAvailableForPurchase !==
          (variant.isAvailableForPurchase || !!variant.availableForPurchase) ||
        availableForPurchaseDate !==
          splitDateTime(variant.availableForPurchase).date ||
        availableForPurchaseTime !==
          splitDateTime(variant.availableForPurchase).time ||
        isPreOrder !== variant.isPreOrder ||
        preOrderDate !== splitDateTime(variant.preOrder).date ||
        preOrderTime !== splitDateTime(variant.preOrder).time
      ) {
        const variables = getProductVariantAvailabilityVariables({
          availableForPurchase: joinDateTime(
            availableForPurchaseDate,
            availableForPurchaseTime
          ),
          isAvailableForPurchase,
          preOrder: isPreOrder ? joinDateTime(preOrderDate, preOrderTime) : "",
          isPreOrder,
          variantId: variant.id
        });

        const availabilityResult = await setProductVariantAvailability(
          variables
        );
        errors = [
          ...errors,
          ...availabilityResult.data.productVariantSetAvailabilityForPurchase
            .errors
        ];
      }

      return errors;
    };
  }

  const handleSubmit = createMetadataUpdateHandler(
    variant,
    createUpdateHandler(variant, variables =>
      setProductVariantAvailability({ variables })
    ),
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );

  // const productIns = useInstagramsByProductsId({
  //   variables: {
  //     id:productId,
  //     pubfilter:true
  //   }
  // })

  const [erpProductVariant, getErpProductVariantQuery] = React.useState<any>(
    null
  );

  const [getErpProductVariantData] = useLazyQuery(getErpProductVariant, {
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data?.erpProductVariant?.id) {
        getErpProductVariantQuery(data?.erpProductVariant);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Sku was successfully loaded from erp.",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Failed to load from erp.",
            description: "snackbar text"
          })
        });
      }
    }
  });

  function onGetErpSkuData(erpSku: string) {
    getErpProductVariantData({ variables: { sku: String(erpSku) } });
  }
  React.useEffect(() => {
    getErpProductVariantQuery(erpProductVariant);
  }, [erpProductVariant]);

  const [imageVisibleEditByUrl] = useVariantImageVisibleEditByUrlMutation({
    onCompleted: data => {
      if (data.variantImageVisibleEditByUrl.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Image Visible Edit Success"
          })
        });
        refetch();
      }
    }
  });
  return (
    <>
      <WindowTitle title={data?.productVariant?.name} />
      <ProductVariantPage
        // 配镜方案下拉框

        extraChargeCategory={extraChargeCategory}
        setExtraChargeCategory={setExtraChargeCategory}
        setNewSku={setNewSku}
        extraChargeCategoryData={extraChargeCategoryData?.data}
        activeSkuExtraChargeCategory={(id, isActive) => {
          activeSkuExtraChargeCategory({
            variables: {
              id,
              isActive
            }
          });
        }}
        skuExtraChargeCategory={
          skuExtraChargeCategory?.data?.skuExtraChargeCategory
        }
        // end
        defaultWeightUnit={shop?.defaultWeightUnit}
        defaultVariantId={data?.productVariant?.product?.defaultVariant?.id}
        cornermarks={(searchCornermarkOpts.data?.search.edges || []).map(
          edge => edge.node
        )}
        extracharges={(searchExtrachargeOpts.data?.search.edges || []).map(
          edge => edge.node
        )}
        errors={errors}
        onSetDefaultVariant={onSetDefaultVariant}
        saveButtonBarState={updateVariantOpts.status}
        loading={disableFormSave}
        placeholderImage={placeholderImg}
        variant={variant}
        images={maybe(() => variant?.images)}
        header={variant?.name || variant?.sku}
        fetchCornermarks={searchCornermark}
        fetchExtracharges={searchExtracharge}
        fetchMoreCornermarks={{
          hasMore: searchCornermarkOpts.data?.search.pageInfo.hasNextPage,
          loading: searchCornermarkOpts.loading,
          onFetchMore: loadMoreCornermarks
        }}
        fetchMoreExtracharges={{
          hasMore: searchExtrachargeOpts.data?.search.pageInfo.hasNextPage,
          loading: searchExtrachargeOpts.loading,
          onFetchMore: loadMoreExtracharges
        }}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        onAdd={() => navigate(productVariantAddUrl(productId))}
        onBack={handleBack}
        onDelete={() => openModal("remove")}
        onImageReorder={handleImageReorder}
        onImageSetFirst={handleImageSetFirst}
        onImageUpload={handleImageUpload}
        onImageFetch={handleImageFetch}
        onImageEdit={handleImageEdit}
        onImageDelete={handleImageDelete}
        onSubmit={handleSubmit}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantClick={variantId => {
          navigate(productVariantEditUrl(productId, variantId));
        }}
        onVariantReorder={handleVariantReorder}
        // productIns={productIns?.data?.instagramsByProductsId}
        onGetErpData={onGetErpSkuData}
        erpProductVariant={erpProductVariant}
        onVisibleEdit={id =>
          imageVisibleEditByUrl({
            variables: {
              id
            }
          })
        }
      />
      <ProductVariantDeleteDialog
        confirmButtonState={deleteVariantOpts.status}
        onClose={() => navigate(productVariantEditUrl(productId, variantId))}
        onConfirm={() =>
          deleteVariant({
            variables: {
              id: variantId
            }
          })
        }
        open={params.action === "remove"}
        name={data?.productVariant?.name}
      />
    </>
  );
};
export default ProductVariant;

export function createImageUploadHandler(
  id: string,
  createVariantImage: (variables: VariantImageCreateVariables) => void
) {
  return (file: File) =>
    createVariantImage({
      alt: "",
      image: file,
      variant: id
    });
}

export function createImageReorderHandler(
  variant: ProductVariantDetails_productVariant,
  reorderVariantImages: (variables: VariantImageReorderVariables) => void
) {
  return ({ newIndex, oldIndex }: ReorderEvent) => {
    let ids = variant.images.map(image => image.id);
    ids = arrayMove(ids, oldIndex, newIndex);
    reorderVariantImages({
      imagesIds: ids,
      variantId: variant.id
    });
  };
}
